import { Component, OnInit, Inject, inject } from "@angular/core";
import { OwlOptions } from "ngx-owl-carousel-o";
import { StandardService } from "../services/standard.service";
import { Router, ActivatedRoute } from "@angular/router";
import { LOCAL_STORAGE, WebStorageService } from "ngx-webstorage-service";
import { NrbeStandard } from "../models/nrbestandard";
import { CryptographyService } from "../services/cryptography.service";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { DatePipe, Location } from "@angular/common";
import { NrbesItem } from "../models/nrbesItem";
import { AddendumStatusEnum } from "../core/constant";
import DOMPurify from 'dompurify';

import { SnackbarComponent } from "../snackbar/snackbar.component";
import { MatSnackBar } from "@angular/material/snack-bar";
@Component({
  selector: "app-nrbes-detail-view",
  templateUrl: "./nrbes-detail-view.component.html",
  styleUrls: ["./nrbes-detail-view.component.scss"],
  providers: [DatePipe],
})
export class NrbesDetailViewComponent implements OnInit {
  private _snackBar = inject(MatSnackBar);
  allLocations: string = '';
  subLocations: string = '';
  createdOn: Date;
  detailsExpanded = false;
  additionalInfoExpanded = false;
  standardTypeName: string;

  loading: boolean = false;

  customOptions: OwlOptions = {
    loop: true,
    mouseDrag: true,
    touchDrag: false,
    pullDrag: false,
    dots: false,
    navSpeed: 700,
    navText: [
      '<img src="/assets/images/back.svg">',
      '<img src="/assets/images/next.svg">',
    ],
    responsive: {
      0: {
        items: 1,
      },
      400: {
        items: 1,
      },
      740: {
        items: 1,
      },
      940: {
        items: 1,
      },
    },
    nav: true,
  };

  nrbesId: number = 0;
  nrbesDetail: NrbeStandard = new NrbeStandard();
  error: string = "";
  notes: SafeHtml = "";
  additionalInformation: SafeHtml = "";
  get keyContactEmailResolved(): string {
    const d = (this.nrbesDetail || (this as any).grsDetail) as any;
    return (
      d?.keyContact?.email ||
      d?.keyContact?.Email ||
      d?.standard?.keyContact?.email ||
      d?.standard?.keyContact?.Email ||
      d?.standard?.email ||
      d?.standard?.Email ||
      d?.email ||
      d?.Email ||
      (this as any).keyContactEmail ||
      ""
    );
  }

  get linksList(): any[] {
    const d = (this.nrbesDetail || (this as any).grsDetail) as any;
    const rawLinks = d?.links || d?.standard?.links || d?.Links || d?.standard?.Links || d?.resourceLinks1 || [];
    if (!Array.isArray(rawLinks)) return [];
    return rawLinks.map((l: any) => ({
      url: l?.url || l?.Url || "",
      title: l?.title || l?.Title || l?.url || l?.Url || ""
    })).filter(l => !!l.url?.trim() || !!l.title?.trim());
  }

  // "This Market Standard is based on a Global Approved Solution" banner -
  // shown whenever this market standard was adopted via the "Add to Market
  // Standards" toggle on a Global Approved Solution (not for genuinely
  // local-only standards). Lists every market that adopted the same one.
  get adoptedMarkets(): any[] {
    return this.nrbesDetail?.adoptedMarkets || [];
  }

  get showAdoptionBanner(): boolean {
    return this.adoptedMarkets.length > 0;
  }

  get adoptionBannerTitle(): string {
    return this.adoptedMarkets.length > 1
      ? "This Market Standard is based on a Global Approved Solution Adopted by Following Markets"
      : "This Market Standard is based on a Global Approved Solution.";
  }

  getAdoptionLine(market: any): string {
    const date = this.datePipe.transform(market?.adoptedDate, "d MMMM yyyy");
    const role = market?.adoptedByRole || "Market Admin";
    const name = market?.adoptedByName || "";
    return `Adopted on ${date} by ${market?.countryName} ${role} (${name}).`;
  }

  get hardwareDescription(): string {
    const d = (this.nrbesDetail || (this as any).grsDetail) as any;
    return (
      d?.keyContact?.emailDescription ||
      d?.keyContact?.EmailDescription ||
      d?.standard?.keyContact?.emailDescription ||
      d?.standard?.keyContact?.EmailDescription ||
      d?.standard?.emailDescription ||
      d?.standard?.EmailDescription ||
      d?.emailDescription ||
      d?.EmailDescription ||
      d?.keyContactDesc ||
      d?.standard?.keyContactDesc ||
      ""
    );
  }

  constructor(
    private route: Router,
    private activeRoute: ActivatedRoute,
    @Inject(LOCAL_STORAGE) private storage: WebStorageService,
    private standardService: StandardService,
    private cryptographyService: CryptographyService,
    private sanitizer: DomSanitizer,
    private datePipe: DatePipe,
    private location: Location
  ) {
    this.nrbesId = parseInt(this.activeRoute.snapshot.paramMap.get("nrbesId"));
  }

  ngOnInit() {
    this.getDetailByNrbesId(this.nrbesId);
  }

  /**
   * Get National Standard detail by standard id
   * @return void
   */
  getDetailByNrbesId(grsId: number) {
    this.loading = true;
    const rawCountryId =
      this.storage.get("selectedCountryId") ||
      this.storage.get("countryId") ||
      this.storage.get("adCountryId");
    const decryptedId = rawCountryId
      ? this.cryptographyService.decryptData(rawCountryId)
      : null;
    const countryIds = decryptedId != null && !isNaN(Number(decryptedId)) ? [Number(decryptedId)] : null;

    this.standardService
      .getNrbesDetailInfomationByNrbesId(
        grsId,
        countryIds,
        this.standardService.nrbesFiltrations.filterSet,
        this.standardService.nrbesFiltrations.searchKey
      )
      .subscribe((data: any) => {
        void 0;
        var options = {
          /* … */
        };
        try {
          this.nrbesDetail = new NrbeStandard(data);
          this.allLocations = this.nrbesDetail.standard.standardLocation.length > 0 ? this.nrbesDetail.standard.standardLocation.map((loc: any) => loc.locationType.name)
            .join(", ") : this.nrbesDetail.standard.locationType.name;
          this.subLocations = this.nrbesDetail.standard.standardLocation
            .map((loc: any) =>
              loc.locationType.subLocationTypes?.length
                ? loc.locationType.subLocationTypes.map((sloc: any) => sloc.name).join(", ")
                : "",
            )
            .filter(Boolean)
            .join(", ");
        } catch (error) {
          void 0;
        }
        const cleanNotes = DOMPurify.sanitize(this.nrbesDetail.standard.note);

        this.notes = this.sanitizer.bypassSecurityTrustHtml(cleanNotes);

        const cleanAddInfo = DOMPurify.sanitize(
          this.nrbesDetail.standard.additionalInformation
        );
        this.additionalInformation =
          this.sanitizer.bypassSecurityTrustHtml(cleanAddInfo);

        // this.notes = this.sanitizer.bypassSecurityTrustHtml(
        //   this.nrbesDetail.standard.note
        // );
        // this.additionalInformation = this.sanitizer.bypassSecurityTrustHtml(
        //   this.nrbesDetail.standard.additionalInformation
        // );
        this.loading = false;
        (error: string) => {
          this.error = error;
          this.loading = false;
        };
      });
  }
  navigateAccToType(type, id) {
    if (type == 4) {
      this.route.navigate(["/minimum-standard-detail-view/" + id]);
      return;
    }
    if (type == 8) {
      this.route.navigate(["/operations-standards-detail-view/" + id]);
      return;
    }
    if (type == 0) {
      this.route.navigate(["/view-equipments-standard-detail/" + id]);
      return;
    }
    if (type == 1 || type == 2) {
      this.route.navigate(["/global-recommendations-detail-view/" + id]);
      return;
    }
  }
  //need to redirect correct component with checking standard type
  slide(sID: number, isForward: boolean) {
    this.loading = true;

    this.standardService
      .getNextPreviousNrbsInfomationByCurrentNrbesId(
        sID,
        isForward,
        [
          this.cryptographyService.decryptData(
            this.storage.get("selectedCountryId")
          ),
        ],
        this.standardService.nrbesFiltrations.filterSet,
        this.standardService.nrbesFiltrations.searchKey
      )
      .subscribe((data: any) => {
        this.route.navigate(["nrbes-view", data.id]);
        this.nrbesDetail = new NrbeStandard(data);

        var options = {
          /* … */
        };

        const cleanNotes = DOMPurify.sanitize(this.nrbesDetail.standard.note);

        this.notes = this.sanitizer.bypassSecurityTrustHtml(cleanNotes);

        const cleanAddInfo = DOMPurify.sanitize(
          this.nrbesDetail.standard.additionalInformation
        );
        this.additionalInformation =
          this.sanitizer.bypassSecurityTrustHtml(cleanAddInfo);
        // this.notes = this.sanitizer.bypassSecurityTrustHtml(
        //   this.nrbesDetail.standard.note
        // );
        // this.additionalInformation = this.sanitizer.bypassSecurityTrustHtml(
        //   this.nrbesDetail.standard.additionalInformation
        // );
        this.loading = false;
        (error: string) => {
          this.error = error;
          this.loading = false;
        };
      });
  }
  downloadExcel() {
    this.standardService.downloadSingleStandardExcel(
      this.nrbesDetail,
      "market"
    );
    return;
  }

  goToAddendum(nrbesItem: any): void {
    void 0;
    try {
      this.storage.set("adCountryId", nrbesItem.country);
      this.route.navigate(["/addendum/" + nrbesItem.availabileAddendumId]);
    } catch (e) {
      void 0;
    }
  }

  openSnackBar() {
    this._snackBar.openFromComponent(SnackbarComponent, {
      duration: 2 * 1000,
      data: {
        message: "Object Copied to clipboard!",
      },
    });
  }

  toggleDetails() {
    this.detailsExpanded = !this.detailsExpanded;
  }
  toggleDetails2() {
    this.additionalInfoExpanded = !this.additionalInfoExpanded;
  }

  copyText(content: any) {
    void 0;
    navigator.clipboard.writeText(content);
    this.openSnackBar();
  }

  getDetails() {
    return this.sanitizer.bypassSecurityTrustHtml(this.nrbesDetail.standard.note);
  }
  getAdditionalInfo() {
    return this.sanitizer.bypassSecurityTrustHtml(
      this.nrbesDetail.standard.additionalInformation,
    );
  }
  goBack() {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.route.navigate(["/view-all-standards"]);
    }
  }

  get isSameDescription(): boolean {
    const newDesc = this.nrbesDetail?.standard?.newRestaurantDescription?.trim();
    const existingDesc = this.nrbesDetail?.standard?.description?.trim();

    return newDesc && existingDesc && newDesc === existingDesc;
  }

  get hasNewDescription(): boolean {
    return !!this.nrbesDetail?.standard?.newRestaurantDescription;
  }

  get hasExistingDescription(): boolean {
    return !!this.nrbesDetail?.standard?.description;
  }
}
